"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
function RecursiveFooterMenuList({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={depth === 0 ? "space-y-2.5" : "mt-2 space-y-2 pl-3 border-l border-border/40"}>
      {items.map((item, i) => <li key={item.id || `${item.label}-${i}`} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xs">
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterMenuList items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterNavigationConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const accountUrl = useWooAccountUrl();
  const defaultLinks = [{
    id: "def-home",
    label: "Home",
    href: "/"
  }, {
    id: "def-shop",
    label: "Shop Catalog",
    href: "/shop"
  }, {
    id: "def-contact",
    label: "Contact Support",
    href: "/ContactPage"
  }, {
    id: "def-account",
    label: "My Account",
    href: accountUrl
  }];
  if (loading) {
    return <div className="space-y-2.5">
        <div className="h-4 w-28 bg-muted/40 rounded-xs animate-pulse" />
        <div className="h-4 w-32 bg-muted/40 rounded-xs animate-pulse" />
        <div className="h-4 w-24 bg-muted/40 rounded-xs animate-pulse" />
        <div className="h-4 w-36 bg-muted/40 rounded-xs animate-pulse" />
      </div>;
  }
  const itemsToRender = menuItems && menuItems.length > 0 ? menuItems : defaultLinks;
  return <RecursiveFooterMenuList items={itemsToRender} />;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" data-nav="dark" className="dark bg-background text-foreground border-t border-border">
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Columns 1–4: Business summary & direct contact credentials */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="w-fit">
              <Link to="/" aria-label="AQUAFLAME Home">
                <WvcLogo className="h-10 w-auto" />
              </Link>
            </div>

            <p className="text-sm text-foreground/80 leading-relaxed max-w-sm">
              AQUAFLAME is an online retailer of residential natural gas water heaters and parts, operated by Domain Technology (Hong Kong) Co., Limited.
            </p>

            <div className="space-y-3 pt-2">
              <p className="text-sm font-medium text-foreground">
                Domain Technology (Hong Kong) Co., Limited
              </p>

              <div className="flex items-start gap-3 text-sm text-foreground/85">
                <MapPin className="size-4 shrink-0 text-primary mt-1" aria-hidden="true" />
                <span className="leading-snug">
                  Man On Building, 18–20 Ho Man Tin Street, Kowloon City District, Kowloon
                </span>
              </div>

              <div className="flex items-center gap-3 text-sm text-foreground/85">
                <Mail className="size-4 shrink-0 text-primary" aria-hidden="true" />
                <a href="mailto:carry6891@outlook.com" className="hover:text-foreground transition-colors underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xs">
                  carry6891@outlook.com
                </a>
              </div>
            </div>
          </div>

          {/* Columns 5–7: Quick site navigation */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            <h3 className="font-serif text-sm font-semibold tracking-wider uppercase text-foreground/90">
              Quick Links
            </h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterNavigationConsumer />
            </MenuProvider>
          </div>

          {/* Columns 8–10: Customer support */}
          <div className="lg:col-span-3 flex flex-col gap-5">
            <h3 className="font-serif text-sm font-semibold tracking-wider uppercase text-foreground/90">
              Customer Support
            </h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm text-foreground/85">
                <Mail className="size-4 shrink-0 text-primary mt-1" aria-hidden="true" />
                <div className="space-y-1">
                  <p className="font-medium text-foreground">Email Support</p>
                  <p className="text-xs text-foreground/75 leading-relaxed">
                    We reply to emails within 1–2 business days.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Columns 11–12: Social channels */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <h3 className="font-serif text-sm font-semibold tracking-wider uppercase text-foreground/90">
              Connect
            </h3>

            <div className="flex items-center gap-2">
              <Link to="https://facebook.com" newTab aria-label="AQUAFLAME on Facebook" className="size-9 rounded-md border border-border/80 bg-card/40 flex items-center justify-center text-foreground/80 hover:text-foreground hover:bg-card hover:border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Facebook className="size-4" aria-hidden="true" />
              </Link>
              <Link to="https://twitter.com" newTab aria-label="AQUAFLAME on X" className="size-9 rounded-md border border-border/80 bg-card/40 flex items-center justify-center text-foreground/80 hover:text-foreground hover:bg-card hover:border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Twitter className="size-4" aria-hidden="true" />
              </Link>
              <Link to="https://instagram.com" newTab aria-label="AQUAFLAME on Instagram" className="size-9 rounded-md border border-border/80 bg-card/40 flex items-center justify-center text-foreground/80 hover:text-foreground hover:bg-card hover:border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Instagram className="size-4" aria-hidden="true" />
              </Link>
              <Link to="https://linkedin.com" newTab aria-label="AQUAFLAME on LinkedIn" className="size-9 rounded-md border border-border/80 bg-card/40 flex items-center justify-center text-foreground/80 hover:text-foreground hover:bg-card hover:border-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Linkedin className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-border/80" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-foreground/70">
          <p>
            {"© "}{currentYear}{" AQUAFLAME. All rights reserved. Professional Gas Water Heating Equipment."}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/shop" className="hover:text-foreground transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xs">
              Shop Catalog
            </Link>
            <Link to="/ContactPage" className="hover:text-foreground transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xs">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </section>;
}